import { useState, useEffect } from 'react';

import { getIssuedManual } from 'views/App/Portal/Policy/IssuedManuals/_data_sources/useIssuedManual';
import type { Manual } from 'views/App/Portal/Policy/_types/manuals/draft';
import type { Section, SubSection } from 'views/App/Portal/Policy/_types/policy';
import type { ContentReference } from '../../_types/dtb';

export type ResolvedReference = {
  name: string;
  content: string | null;
};

export const parseContentUri = (contentUri: string) => {
  const match = contentUri.match(/(?:xref:\/\/)?manuals?\/([^/]+)\/content\/([^/]+)/);
  if (match) {
    return { manualId: match[1], contentId: match[2] };
  }
  return null;
};

export const findContentNode = (
  manual: Manual,
  contentId: string
): { content: string | null; name: string | null; } | null => {
  const preface = manual.prefaces?.find((p: any) => p.id === contentId);
  if (preface) return { content: preface.content ?? null, name: preface.title ?? null };

  for (const chapter of manual.chapters || []) {
    for (const policy of chapter.policies || []) {
      const policyName = [policy.key, policy.title].filter(Boolean).join(': ');
      if (policy.id === contentId) return { content: null, name: policyName };
      for (const section of (policy.sections as Section[]) || []) {
        const sectionName = [section.key, section.title].filter(Boolean).join(': ');
        if (section.id === contentId) return { content: section.content ?? null, name: sectionName };
        for (const sub of (section.subSections as SubSection[]) || []) {
          const subName = [sub.key, sub.title].filter(Boolean).join(': ');
          if (sub.id === contentId) return { content: sub.content ?? null, name: subName };
        }
      }
    }
  }
  return null;
};

export const usePolicyReferenceResolver = (contentReferences: ContentReference[]) => {
  const [resolvedRefs, setResolvedRefs] = useState<ResolvedReference[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!contentReferences.length) {
      setResolvedRefs([]);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    const resolve = async () => {
      const manualCache = new Map<string, Promise<Manual>>();

      const results: ResolvedReference[] = await Promise.all(
        contentReferences.map(async (ref) => {
          const parsed = parseContentUri(ref.contentUri);
          if (!parsed) return { name: ref.name, content: null };

          try {
            if (!manualCache.has(parsed.manualId)) {
              manualCache.set(parsed.manualId, getIssuedManual(parsed.manualId));
            }
            const manual = await manualCache.get(parsed.manualId)!;
            const node = findContentNode(manual, parsed.contentId);
            return { name: node?.name ?? ref.name, content: node?.content ?? null };
          } catch {
            return { name: ref.name, content: null };
          }
        })
      );

      if (!cancelled) {
        setResolvedRefs(results);
        setIsLoading(false);
      }
    };

    resolve();
    return () => {
      cancelled = true;
    };
  }, [contentReferences]);

  return { resolvedRefs, isLoading };
};
